/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';

import { ContactSupport } from '@mui/icons-material';
import BusinessIcon from '@mui/icons-material/Business';
import ComputerIcon from '@mui/icons-material/Computer';
import SupportIcon from '@mui/icons-material/ContactSupportSharp';
import EmailIcon from '@mui/icons-material/Email';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import GavelIcon from '@mui/icons-material/GavelRounded';
import GroupIcon from '@mui/icons-material/Group';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MemoryIcon from '@mui/icons-material/Memory';
import ReorderIcon from '@mui/icons-material/Reorder';
import SettingsIcon from '@mui/icons-material/Settings';
import SmsFailedIcon from '@mui/icons-material/SmsFailed';
import StorageIcon from '@mui/icons-material/Storage';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemButtonProps,
  ListItemIcon,
  ListItemText,
} from '@mui/material';

import { adminRoutes } from '../../constants/adminRoutes';
import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { useObjectState } from '../../utils/useObjectState';
import { NavigationDrawer, NavigationDrawerProps } from '../NavigationDrawer/NavigationDrawer';
import { NavigationHeader } from '../NavigationHeader/NavigationHeader';
import { useAdminNavigationStyles } from './AdminNavigation.styles';

const ListItemLink = memo<ListItemButtonProps<typeof NavLink>>(props => (
  <ListItemButton {...props} component={NavLink} />
));

type CollapseState = {
  datasets: boolean;
  managePlans: boolean;
  systemConfig: boolean;
};

export function AdminNavigation(props: NavigationDrawerProps) {
  const { drawerWidth, drawerOpen } = props;
  const classes = useAdminNavigationStyles();
  const { t } = useTranslation([Namespaces.Admin]);
  const location = useLocation();

  const isSelectedPath = (path: string) => location.pathname === `${BasicRoutes.ADMIN}/${path}`;

  const [collapseState, setCollapseState] = useObjectState<CollapseState>(() => {
    const initial: CollapseState = {
      datasets: false,
      managePlans: false,
      systemConfig: false,
    };

    if (
      [
        adminRoutes.ADMIN_MANAGE_PLANS_COMPUTE,
        adminRoutes.ADMIN_MANAGE_PLANS_STORAGE,
        adminRoutes.ADMIN_MANAGE_PLANS_SUPPORT,
      ].some(isSelectedPath)
    ) {
      initial.managePlans = true;
    }

    if ([adminRoutes.ADMIN_DATASETS, adminRoutes.ADMIN_DATASETS_CATEGORIES].some(isSelectedPath)) {
      initial.datasets = true;
    }

    if (
      [
        adminRoutes.ADMIN_SYSTEM_CONFIG,
        adminRoutes.ADMIN_EMAIL_CONFIG,
        adminRoutes.ADMIN_SUPPORT_CONFIG,
        adminRoutes.ADMIN_IMPRINT_CONFIG,
      ].some(isSelectedPath)
    ) {
      initial.systemConfig = true;
    }

    return initial;
  });

  const handleCollapsibleItemClick = (item: keyof CollapseState) => {
    setCollapseState({ ...collapseState, [item]: !collapseState[item] });
  };

  return (
    <NavigationDrawer drawerOpen={drawerOpen} drawerWidth={drawerWidth}>
      <div className={classes.root}>
        <NavigationHeader to={BasicRoutes.HOME} />

        <div className={classes.content}>
          <List component="nav" aria-labelledby="nested-list-subheader">
            <ListItemLink to={`../${adminRoutes.ADMIN_USERS}`} selected={isSelectedPath(adminRoutes.ADMIN_USERS)}>
              <ListItemIcon className={classes.listItemIcon}>
                <GroupIcon />
              </ListItemIcon>
              <ListItemText primary={t('navigation.users')} />
            </ListItemLink>

            <ListItemLink
              to={`../${adminRoutes.ADMIN_ORGANIZATIONS}`}
              selected={isSelectedPath(adminRoutes.ADMIN_ORGANIZATIONS)}
            >
              <ListItemIcon className={classes.listItemIcon}>
                <BusinessIcon />
              </ListItemIcon>
              <ListItemText primary={t('navigation.organizations')} />
            </ListItemLink>
            <ListItemLink
              to={`../${adminRoutes.ADMIN_CLUSTERS_ORPHANS}`}
              selected={isSelectedPath(adminRoutes.ADMIN_CLUSTERS_ORPHANS)}
            >
              <ListItemIcon className={classes.listItemIcon}>
                <ComputerIcon />
              </ListItemIcon>
              <ListItemText primary={t('navigation.orphans')} />
            </ListItemLink>
            <ListItemLink
              to={`../${adminRoutes.ADMIN_ENQUIRIES}`}
              selected={isSelectedPath(adminRoutes.ADMIN_ENQUIRIES)}
            >
              <ListItemIcon className={classes.listItemIcon}>
                <SmsFailedIcon />
              </ListItemIcon>
              <ListItemText primary={t('navigation.enquiries')} />
            </ListItemLink>

            <Divider component="div" sx={{ mt: '10px', mb: '10px' }} />

            <ListItemButton onClick={() => handleCollapsibleItemClick('datasets')}>
              <ListItemIcon className={classes.listItemIcon}>
                <StorageIcon />
              </ListItemIcon>
              <ListItemText primary={t('navigation.datasets')} />
              {collapseState.datasets ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse
              in={collapseState.datasets}
              timeout="auto"
              unmountOnExit
              className={classes.nestedCollapsibleOffset}
            >
              <List component="div" disablePadding>
                <ListItemLink
                  to={`../${adminRoutes.ADMIN_DATASETS}`}
                  selected={isSelectedPath(adminRoutes.ADMIN_DATASETS)}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <ListAltIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.datasets')} />
                </ListItemLink>
                <ListItemLink
                  to={`../${adminRoutes.ADMIN_DATASETS_CATEGORIES}`}
                  selected={isSelectedPath(adminRoutes.ADMIN_DATASETS_CATEGORIES)}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <LibraryBooksIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.datasets_categories')} />
                </ListItemLink>
              </List>
            </Collapse>

            <ListItemButton onClick={() => handleCollapsibleItemClick('managePlans')}>
              <ListItemIcon className={classes.listItemIcon}>
                <ReorderIcon />
              </ListItemIcon>
              <ListItemText primary={t('navigation.manage_plans')} />
              {collapseState.managePlans ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse
              in={collapseState.managePlans}
              timeout="auto"
              unmountOnExit
              className={classes.nestedCollapsibleOffset}
            >
              <List component="div" disablePadding>
                <ListItemLink
                  to={`../${adminRoutes.ADMIN_MANAGE_PLANS_COMPUTE}`}
                  selected={isSelectedPath(adminRoutes.ADMIN_MANAGE_PLANS_COMPUTE)}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <MemoryIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.compute_plans')} />
                </ListItemLink>
                <ListItemLink
                  to={`../${adminRoutes.ADMIN_MANAGE_PLANS_STORAGE}`}
                  selected={isSelectedPath(adminRoutes.ADMIN_MANAGE_PLANS_STORAGE)}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <StorageIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.storage_plans')} />
                </ListItemLink>
                <ListItemLink
                  to={`../${adminRoutes.ADMIN_MANAGE_PLANS_SUPPORT}`}
                  selected={isSelectedPath(adminRoutes.ADMIN_MANAGE_PLANS_SUPPORT)}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <ContactSupport />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.support_plans')} />
                </ListItemLink>
              </List>
            </Collapse>

            <ListItemButton onClick={() => handleCollapsibleItemClick('systemConfig')}>
              <ListItemIcon className={classes.listItemIcon}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary={t('navigation.system-config')} />
              {collapseState.systemConfig ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse
              in={collapseState.systemConfig}
              timeout="auto"
              unmountOnExit
              className={classes.nestedCollapsibleOffset}
            >
              <List component="div" disablePadding>
                <ListItemLink
                  to={`../${adminRoutes.ADMIN_SYSTEM_CONFIG}`}
                  selected={isSelectedPath(adminRoutes.ADMIN_SYSTEM_CONFIG)}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <TuneIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.general')} />
                </ListItemLink>
                <ListItemLink
                  to={`../${adminRoutes.ADMIN_EMAIL_CONFIG}`}
                  selected={isSelectedPath(adminRoutes.ADMIN_EMAIL_CONFIG)}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.email')} />
                </ListItemLink>
                <ListItemLink
                  to={`../${adminRoutes.ADMIN_SUPPORT_CONFIG}`}
                  selected={isSelectedPath(adminRoutes.ADMIN_SUPPORT_CONFIG)}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <SupportIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.support_configuration')} />
                </ListItemLink>
                <ListItemLink
                  to={`../${adminRoutes.ADMIN_IMPRINT_CONFIG}`}
                  selected={isSelectedPath(adminRoutes.ADMIN_IMPRINT_CONFIG)}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <GavelIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('navigation.imprint')} />
                </ListItemLink>
              </List>
            </Collapse>
          </List>
        </div>
      </div>
    </NavigationDrawer>
  );
}
